import { pathFromName } from "named-paths"
import { getNestedValue, hasNestedValue } from "nested-values"
import { titleize } from "inflection"

import {
  deleteElement,
  reset,
  setValue,
  submitError,
  submitFinish,
  submitStart,
  validationError,
} from "./Store"

import invariant from "invariant"

export const VALIDATION_MESSAGES = {
  requires: "can't be blank",
}

// Manage pending changes that will be submitted as input to a GraphQL
// migration.
//
// Components will be given a Mutation instance via props or HOC and should use
// the intent indicating functions to request state change without regard to
// how the state is actually persisted.
//
// Each root View will receive a _new_ UiState tied to the page's position in
// the history stack.
export default class Mutation {
  /**
   * @param [Function] dispatch function for redux store.
   * @param [Function] getState function to retrieve the current state from the
   * redux store.
   * @param [String] mutationKey unique value for the instance of the migration
   * changes. Typically derived from the {UiState.locationKey}.
   * @param [Object] root the current state of the object to be mutated.
   */
  constructor(dispatch, getState, mutationKey, root = {}) {
    this._dispatch = dispatch
    this.getState = getState
    this._clientValidations = []

    if (!mutationKey) {
      throw "No mutationKey"
    }

    this.mutationKey = mutationKey
    this.root = root
  }

  /**
   * Update the store when nothing else has chaned.
   */
  attach(dispatch, getState) {
    this._dispatch = dispatch
    this.getState = getState
  }

  /**
   * Gets the current value of the field at the given path if changed or the
   * current value of the field from the root object.
   *
   * @param [Array<String>] path as an array of segments from the root.
   * @param [String] defaultValue value if the stored value is undefined.
   * @return [String] value at the given path.
   */
  get(path, defaultValue) {
    path = pathFromName(path)

    return getNestedValue(
      path,
      this._changes(),
      this.root ? () => getNestedValue(path, this.root, defaultValue) : defaultValue
    )
  }

  /**
   * Indicates if the mutation has a change at the given path.
   *
   * @param [Array<String>] path as an array of segments from the root.
   * @return [Boolean] true if there a value has been set at the given path
   * (even if the value is undefined or null)
   */
  has(path) {
    path = pathFromName(path)

    return hasNestedValue(path, this._changes())
  }

  /**
   * Dispatches an action to change the value in the redux store. The changed
   * value may not be immediately visible to `get`.
   *
   * @param [Array<String>] path as an array of segments from root.
   * @param [Object] value the value of the field to set.
   */
  set(path, value) {
    invariant(!this.isSubmitting(), "Cannot modify a mutation while submitting")

    path = pathFromName(path)

    const currentValue = this.root && getNestedValue(path, this.root)
    if (typeof value === "boolean") {
      if (value === false && currentValue === undefined) {
        value = undefined
      }
    } else if (value == currentValue || (value === "" && !currentValue)) {
      value = undefined
    }

    const payload = {
      mutationKey: this.mutationKey,
      path,
      value,
    }

    this._dispatch(setValue(payload))
  }

  /**
   * Resets the pending changes.
   */
  reset() {
    const payload = { mutationKey: this.mutationKey }
    this._resetErrorCache()
    this._dispatch(reset(payload))
  }

  /**
   * Get the set of current changes.
   */
  changes() {
    return this._clone(this._changes())
  }

  /**
   * Submits the pending {#changes} to the apollo `mutate` supplied method.
   *
   * @param [Function] graphMutateFunc
   * @param [Boolean] root the root element of the `variables` argument to assign
   * changes too. When absent changes are assigned directly to the `variables`
   * argument. Default is 'input' to match Relay calling conventions.
   * @param [Object] data additional data to include in the change payload.
   * @return Promise
   */
  submit(graphMutateFunc, { root = "input", data } = {}) {
    invariant(!this.isSubmitting(), "Cannot submit while a request is already in progress")

    const changes = { ...(this.changes() || {}), ...(data || {}) }
    changes.id = changes.id || (this.root && this.root.id)

    return this._dispatch(dispatch => {
      dispatch(submitStart({ mutationKey: this.mutationKey, timestamp: new Date() }))

      const variables = root ? { variables: { [root]: changes } } : { variables: changes }

      return graphMutateFunc(variables)
        .then(result => {
          this._resetErrorCache()

          dispatch(
            submitFinish({
              mutationKey: this.mutationKey,
              timestamp: new Date(),
            })
          )

          return result
        })
        .catch(error => {
          this._resetErrorCache()

          // TODO track communication errors
          // https://trello.com/c/RCm0namh

          dispatch(
            submitError({
              mutationKey: this.mutationKey,
              error,
              timestamp: new Date(),
            })
          )
          throw error
        })
    })
  }

  /**
   * @return [Boolean] true if the mutation is being submitted.
   */
  isSubmitting() {
    const mutation = this._mutation()
    return mutation.submittedAt && !mutation.submitFinishedAt
  }

  /**
   * Add a client side validation rule that will be checked with calls to
   * {#validate}.
   *
   * @param [Array<String>] path as an array of segments from root.
   * @param [Function] func that returns a validation error if the validation
   * conditions are not met.
   *
   *     validateFunc( value, path, mutation )
   *
   * @return [Mutation] returns this for easy chaining.
   */
  validates(path, func) {
    path = pathFromName(path)

    let validator = this._clientValidations[path]
    if (!validator) {
      this._clientValidations[path] = validator = { path, funcs: [] }
    }

    if (validator.funcs.indexOf(func) < 0) {
      validator.funcs.push(func)
    }

    return this
  }

  /**
   * Require a value at the given path.
   */
  requires(path, message) {
    return this.validates(path, value => {
      if (!value) {
        return message || VALIDATION_MESSAGES.requires
      }

      return null
    })
  }

  /**
   * Execute all client validations and dispatches messages to update the
   * validation state.
   *
   * @return [Boolean] true if all the client side validations have passed.
   */
  validate() {
    const allValid = true

    for (const key in this._clientValidations) {
      const validator = this._clientValidations[key]
      const value = this.get(validator.path)

      validator.funcs.forEach(f => {
        let error = f(value, validator.path, this)

        if (error) {
          const field = validator.path[validator.path.length - 1]

          if (typeof error === "string") {
            error = {
              message: error,
              fullMessage: `${titleize(field)} ${error}`,
            }
          }

          const action = validationError({
            mutationKey: this.mutationKey,
            field,
            path: validator.path.slice(0, validator.path.length - 1),
            ...error,
          })

          this._dispatch(action)
        }
      })
    }

    return allValid
  }

  /**
   * Gets validation errors for the mutation.
   *
   * @param [Array<String>] patah as an array of segments from root to limit
   * the errors to.
   * @return [Array<Object>]
   */
  validationErrors(path) {
    if (!this._error() && !this._clientErrors()) {
      return undefined
    }

    const validationErrors = this._validationErrors()

    if (path) {
      path = pathFromName(path)

      return validationErrors.filter(e => this._isFieldError(path, e))
    } else {
      return validationErrors.filter(e => this._isGeneralError(path, e))
    }
  }

  _validationErrors() {
    if (!this._validationErrorsCache) {
      const clientErrors = this._clientErrors() || []
      let validationErrors = [...clientErrors]
      this._error().graphQLErrors.forEach(e => {
        if (e.validationErrors) {
          validationErrors = validationErrors.concat(e.validationErrors)
        }
      })

      this._validationErrorsCache = validationErrors
    }

    return this._validationErrorsCache
  }

  _isFieldError(path, error) {
    if (error.path.length === path.length - 1 && error.field === path[path.length - 1]) {
      for (let ix = 0; ix < error.path.length; ix++) {
        if (error.path[ix] !== path[ix]) {
          return false
        }
      }

      return true
    }

    return false
  }

  _isGeneralError(path, error) {
    if (!error.field) {
      return true
    }

    const fieldPath = [...(error.path || []), error.field]
    const general = !this.has(fieldPath)
    return general
  }

  /**
   * Get any generic or communication errors.
   */
  errors() {
    if (!this._error()) {
      return undefined
    }

    return this._errors()
  }

  _errors() {
    if (!this._errorsCache) {
      const errors = []
      this._error().graphQLErrors.forEach(e => {
        if (!e.validationErrors) {
          errors.push(e.message)
        }
      })

      this._errorsCache = errors
    }

    return this._errorsCache
  }

  _resetErrorCache() {
    delete this._errorsCache
    delete this._validationErrorsCache
  }

  /**
   * Indicates if there are any pending changes.
   *
   * If `path` is provided, returns true only if there are changes at or below
   * that node.
   *
   * @param [Array<String>] path as an array of segments from root.
   * @return [Boolean]
   */
  isDirty(path) {
    const changes = this._changes()
    if (!path) {
      return this._isDirty(changes)
    }

    path = pathFromName(path)
    return this._isDirty(getNestedValue(path, changes))
  }

  _isDirty(obj) {
    if (typeof obj === "object") {
      return Object.keys(obj).length > 0
    }

    return !!obj
  }

  _clone(obj) {
    // undefined, null, 0, "", false -- all scalar values so we can just return
    // the original
    if (!obj || typeof obj !== "object") {
      return obj
    }

    if (obj.map) {
      return obj.map(v => this._clone(v))
    }

    const cloned = {}

    Object.keys(obj).forEach(k => (cloned[k] = this._clone(obj[k])))

    return cloned
  }

  /**
   * Dispatches an action to delete the element at the given path in the redux
   * store. The change may not be immediately visible to `get`.
   *
   * @param [Array<String>] path as an array of segments from root.
   */
  delete(path) {
    const payload = {
      mutationKey: this.mutationKey,
      path: pathFromName(path),
    }

    this._dispatch(deleteElement(payload))
  }

  _changes() {
    const mutation = this._mutation()
    return mutation && mutation.changes
  }

  _error() {
    const mutation = this._mutation()
    return mutation && mutation.error
  }

  _clientErrors() {
    const mutation = this._mutation()
    return mutation && mutation.clientErrors
  }

  _mutation() {
    const state = this.getState()
    return (state && state[this.mutationKey]) || {}
  }
}



// WEBPACK FOOTER //
// ../ui-kit/src/lib/store/Mutation/Mutation.js